'use client';

import type React from 'react';

import { Users } from 'lucide-react';
import { cn } from '@skybeam/shared-utils/classnames/cn';
import { utils } from '../../../_components/utils';
import type {
  ResponsivePadding,
  ResponsiveValue,
} from '../../../_components/utils/types';

export type AudienceCardsSectionProps = {
  backgroundColor?: string;
  containerPadding?: ResponsivePadding;
  contentMaxWidth?: ResponsiveValue;
  heading: string;
  items: Array<{ text: string; title: string }>;
  subtitle?: string;
};

const AudienceCard = ({ text, title }: { text: string; title: string }) => (
  <div className="tablet:p-8 laptop:max-w-[432px] laptop:flex-col laptop:gap-y-6 laptop:justify-between laptop:p-10 desktop:max-w-[272px] desktop:justify-normal flex gap-x-4 rounded-2xl bg-blue-50 p-4">
    <div className="laptop:max-w-12 flex h-12 min-w-12 items-center justify-center rounded border border-blue-100 bg-white">
      <Users className="text-gray-400" />
    </div>
    <div className="flex flex-col gap-y-2 text-left">
      <div className="font-proximaNova tablet:text-[26px] laptop:text-3xl text-2xl leading-none font-bold text-blue-700">
        {title}
      </div>
      <div className="text-lg leading-normal">{text}</div>
    </div>
  </div>
);

export const AudienceCardsSection = ({
  backgroundColor = 'white',
  containerPadding,
  contentMaxWidth,
  heading,
  items,
  subtitle,
}: AudienceCardsSectionProps) => {
  const containerPaddingStyle =
    utils.responsiveUtils.getResponsivePaddingStyleTag(containerPadding);
  const contentMaxWidthStyle = utils.responsiveUtils.getResponsiveValueStyleTag(
    contentMaxWidth,
    'max-width',
  );
  const styleTags = [
    containerPaddingStyle.styleTag,
    contentMaxWidthStyle.styleTag,
  ].filter((tag): tag is React.ReactElement => tag !== null);

  return (
    <>
      {styleTags}
      <div
        className={cn(
          'tablet:py-20 desktop:py-24 flex w-full justify-center px-4 py-14',
          containerPaddingStyle.className,
        )}
        style={{ backgroundColor }}
      >
        <div
          className={cn(
            'tablet:gap-y-12 desktop:gap-y-16 flex w-full flex-col items-center gap-y-8 text-center',
            contentMaxWidthStyle.className,
          )}
        >
          <div className="tablet:gap-y-6 flex flex-col gap-y-4">
            <h2 className="heading-marketing-2 font-light">{heading}</h2>
            {subtitle && (
              <div className="tablet:text-lg laptop:text-xl text-base">
                {subtitle}
              </div>
            )}
          </div>
          <div className="laptop:flex-row laptop:flex-wrap flex flex-col gap-4">
            {items.map((item) => (
              <AudienceCard
                key={item.title}
                text={item.text}
                title={item.title}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
